import React from 'react';

/**
 * Performance monitoring utilities for React components
 */

interface PerformanceMetric {
  componentName: string;
  renderTime: number;
  timestamp: number;
  props?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private isEnabled: boolean = process.env.NODE_ENV === 'development';

  /**
   * Start measuring component render time
   */
  startMeasure(componentName: string): () => void {
    if (!this.isEnabled) return () => {};

    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      
      this.recordMetric({
        componentName,
        renderTime,
        timestamp: Date.now(),
      });

      // Log slow renders (> 16ms for 60fps)
      if (renderTime > 16) {
        console.warn(`Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`);
      }
    };
  }

  /**
   * Record a performance metric
   */
  private recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    
    // Keep only last 100 metrics to prevent memory leaks
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }
  }

  /**
   * Get performance metrics for a specific component
   */
  getMetrics(componentName?: string): PerformanceMetric[] {
    if (componentName) {
      return this.metrics.filter(m => m.componentName === componentName);
    }
    return [...this.metrics];
  }

  /**
   * Get average render time for a component
   */
  getAverageRenderTime(componentName: string): number {
    const componentMetrics = this.getMetrics(componentName);
    if (componentMetrics.length === 0) return 0;
    
    const totalTime = componentMetrics.reduce((sum, metric) => sum + metric.renderTime, 0);
    return totalTime / componentMetrics.length;
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics = [];
  }

  /**
   * Enable or disable performance monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Get performance summary
   */
  getSummary(): Record<string, { count: number; avgTime: number; maxTime: number }> {
    const summary: Record<string, { count: number; avgTime: number; maxTime: number }> = {};
    
    this.metrics.forEach(metric => {
      if (!summary[metric.componentName]) {
        summary[metric.componentName] = {
          count: 0,
          avgTime: 0,
          maxTime: 0,
        };
      }
      
      const current = summary[metric.componentName];
      current.count++;
      current.maxTime = Math.max(current.maxTime, metric.renderTime);
    });

    // Calculate averages
    Object.keys(summary).forEach(componentName => {
      summary[componentName].avgTime = this.getAverageRenderTime(componentName);
    });

    return summary;
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * React hook for monitoring component performance
 */
export function usePerformanceMonitor(componentName: string) {
  if (process.env.NODE_ENV !== 'development') {
    return { startMeasure: () => () => {} };
  }

  return {
    startMeasure: () => performanceMonitor.startMeasure(componentName),
  };
}

/**
 * Higher-order component for automatic performance monitoring
 */
export function withPerformanceMonitor<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) {
  const displayName = componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';
  
  function MonitoredComponent(props: P) {
    const endMeasure = performanceMonitor.startMeasure(displayName);
    
    React.useEffect(() => {
      endMeasure();
    });

    return React.createElement(WrappedComponent, props);
  }

  MonitoredComponent.displayName = `withPerformanceMonitor(${displayName})`;
  
  return MonitoredComponent;
}

/**
 * Performance debugging utilities
 */
export const performanceDebug = {
  /**
   * Log current performance summary to console
   */
  logSummary(): void {
    if (process.env.NODE_ENV !== 'development') return;
    
    const summary = performanceMonitor.getSummary();
    console.table(summary);
  },

  /**
   * Log slow components (average render time > threshold)
   */
  logSlowComponents(threshold: number = 16): void {
    if (process.env.NODE_ENV !== 'development') return;
    
    const summary = performanceMonitor.getSummary();
    const slowComponents = Object.entries(summary)
      .filter(([_, stats]) => stats.avgTime > threshold)
      .sort(([_, a], [__, b]) => b.avgTime - a.avgTime);

    if (slowComponents.length > 0) {
      console.warn('Slow components detected:');
      console.table(Object.fromEntries(slowComponents));
    } else {
      console.log('No slow components detected');
    }
  },

  /**
   * Start continuous monitoring (logs summary every interval)
   */
  startContinuousMonitoring(intervalMs: number = 30000): () => void {
    if (process.env.NODE_ENV !== 'development') return () => {};
    
    const interval = setInterval(() => {
      this.logSlowComponents();
    }, intervalMs);

    return () => clearInterval(interval);
  },
};

// Auto-start performance monitoring in development
if (process.env.NODE_ENV === 'development') {
  // Make performance monitor available globally for debugging
  (window as any).__performanceMonitor = performanceMonitor;
  (window as any).__performanceDebug = performanceDebug;
  
  console.log('Performance monitoring enabled. Use __performanceDebug.logSummary() to view metrics.');
}